import Avatar from '@mui/material/Avatar'
import Chip, { type ChipProps } from '@mui/material/Chip'
import { blueGrey, common } from '@mui/material/colors'
import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'

import { ReactComponent as GallonIcon } from 'assets/measures/GallonIcon.svg'
import { ReactComponent as ItemIcon } from 'assets/measures/ItemIcon.svg'
import { ReactComponent as PoundIcon } from 'assets/measures/PoundIcon.svg'
import { ReactComponent as TonIcon } from 'assets/measures/TonIcon.svg'
import { ReactComponent as YardIcon } from 'assets/measures/YardIcon.svg'
import { getWorkOrderDetailsPageUrl } from 'router/routes'
import { MEASURE_TYPES } from 'settings/constants/measure'
import { ALPHA_38_PERCENT, COMMON } from 'theme/colors'
import { getColorWithOpacity } from 'theme/utils'

const { GALLON, TON, YARD, POUND, ITEM } = MEASURE_TYPES

type MeasureIcon = React.FC<React.SVGProps<SVGSVGElement>>

export const MEASURE_TO_ICON_MAP: Record<string, MeasureIcon> = {
  [GALLON]: GallonIcon,
  [YARD]: YardIcon,
  [TON]: TonIcon,
  [POUND]: PoundIcon,
  [ITEM]: ItemIcon,
}

type MeasureChipProps = Omit<ChipProps, 'avatar' | 'label'> & {
  workOrderId?: string
  measureUnit?: string
  label?: string
}

const MeasureChip: React.FC<MeasureChipProps> = ({ workOrderId, measureUnit, label, ...rest }) => {
  const theme = useTheme()
  const measureUnitUpperCase = measureUnit?.toUpperCase()
  const Icon = measureUnitUpperCase && measureUnitUpperCase in MEASURE_TO_ICON_MAP ? MEASURE_TO_ICON_MAP[measureUnitUpperCase] : ItemIcon

  // Dark-mode palette mirrors the design's measure-chip recipe — dark slate
  // chip, 38% white avatar, white icon — so all three layers stay readable on
  // the master-sequence board's dark surface. Light-mode keeps the original.
  const isDark = theme.palette.mode === 'dark'
  // `background.black` is a custom theme key (not in MUI's TypeBackground).
  const backgroundBlack = (theme.palette.background as { black?: string }).black ?? COMMON.black
  const chipBg = isDark ? blueGrey[900] : undefined
  const avatarBg = isDark ? getColorWithOpacity(common.white, 0.38) : `${backgroundBlack}${ALPHA_38_PERCENT}`
  const iconColor = isDark ? common.white : theme.palette.background.paper
  const labelColor = isDark ? common.white : undefined
  const borderColor = isDark ? getColorWithOpacity(common.white, 0.12) : undefined

  const darkSx = isDark ? { bgcolor: chipBg, color: labelColor, borderColor, '& .MuiChip-label': { color: labelColor } } : {}

  const avatar = (
    <Avatar sx={{ backgroundColor: avatarBg }}>
      <SvgIcon inheritViewBox sx={{ fontSize: 12, color: iconColor }}>
        <Icon />
      </SvgIcon>
    </Avatar>
  )

  if (workOrderId) {
    return (
      <Chip
        avatar={avatar}
        component={Link}
        href={getWorkOrderDetailsPageUrl(workOrderId)}
        label={label}
        rel="noopener noreferrer"
        size="small"
        sx={{ cursor: 'pointer', '&:hover .MuiChip-label': { textDecoration: 'underline' }, ...darkSx }}
        target="_blank"
        variant="outlined"
        {...rest}
      />
    )
  }

  return <Chip avatar={avatar} label={label} size="small" sx={darkSx} variant="outlined" {...rest} />
}

export default MeasureChip
